import { fmtDate } from "@/lib/heat";
import { RATING_SCALE, workbookKeyTopics } from "@/lib/topics";
import { monthPlan, nextAssessment, fmtDay, fmtLong } from "@/lib/dates";
import { useWorkbook } from "@/lib/workbook";
import { WbText, WbLine, WbRating, WbCheck } from "@/components/WorkbookFields";
import { NextStepPage } from "@/components/NextStepPage";

export const Blank = ({ label, k, value }) => (
  <div className="flex items-end gap-2">
    <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
    <WbLine k={k} value={value} />
  </div>
);

export const SheetHeader = ({ title, sub, data }) => (
  <header className="flex items-start justify-between gap-4 border-b-2 border-[#1F4266] pb-3">
    <div className="flex items-center gap-3">
      <img src="/flower-logo.png" alt="" className="h-10 w-10 object-contain" />
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{sub}</div>
        <h2 className="text-xl font-extrabold tracking-tight text-[#1F4266]">{title}</h2>
      </div>
    </div>
    <div className="text-right text-xs text-slate-600">
      <div className="font-semibold text-slate-900">{data.name}</div>
      <div>Assessment: {fmtDate(data.submitted_at)}</div>
    </div>
  </header>
);

export const Sheet = ({ children, testId }) => (
  <section data-testid={testId} className="page-break worksheet-sheet mx-auto w-full max-w-[8.5in] rounded-xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm print-plain">
    {children}
  </section>
);

const ScaleKey = ({ compact }) => (
  <ol className={compact ? "grid grid-cols-5 gap-1.5 text-[10px]" : "space-y-1.5 text-sm"} data-testid={compact ? "scale-key-compact" : "scale-key"}>
    {RATING_SCALE.map((r, i) => (
      <li key={r.short} className={`flex items-start gap-2 rounded-md bg-slate-50 ${compact ? "p-1.5 leading-tight" : "px-3 py-2"}`}>
        <span className="font-mono font-bold text-[#1F4266]">{i + 1}</span>
        <span className="text-slate-800">{r.full}</span>
      </li>
    ))}
  </ol>
);

const KeyPage = ({ data, topics }) => {
  const lowest = workbookKeyTopics(topics);
  return (
    <Sheet testId="worksheet-key-page">
      <SheetHeader title="Workbook Key" sub="Growth workbook · quick reference" data={data} />
      <p className="mt-4 text-sm text-slate-700">
        Keep this page next to your monthly worksheets. Choose one to three topics to grow in. You can keep one goal for all three months or choose a different goal each month.
      </p>
      <h3 className="mt-5 font-display text-base font-bold text-slate-900">Your two lowest-rated topics in each area</h3>
      <table className="mt-2 w-full text-sm" data-testid="key-lowest-topics">
        <thead>
          <tr className="border-b border-slate-300 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="py-1.5 pr-2">Topic</th><th className="py-1.5 pr-2">Area</th><th className="py-1.5 pr-2">You rated yourself</th><th className="py-1.5">Next level</th>
          </tr>
        </thead>
        <tbody>
          {lowest.map((t) => (
            <tr key={t.key} className="border-b border-slate-100 align-top" data-testid={`key-topic-${t.key}`}>
              <td className="py-1.5 pr-2 font-semibold text-slate-900">{t.label}</td>
              <td className="py-1.5 pr-2 text-slate-600">{t.areaNames}</td>
              <td className="py-1.5 pr-2 text-slate-700">{t.answerFull}</td>
              <td className="py-1.5 font-medium text-[#1F4266]">{t.next}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3 className="mt-5 font-display text-base font-bold text-slate-900">Self-rating scale</h3>
      <p className="text-xs text-slate-500">From lowest to highest.</p>
      <div className="mt-2"><ScaleKey /></div>
      <div className="mt-5 rounded-lg border border-slate-300 p-3 text-sm text-slate-700">
        <span className="font-semibold text-slate-900">Next assessment goal date: </span>
        <span data-testid="next-assessment-date" className="font-semibold text-[#1F4266]">{fmtLong(nextAssessment(data.submitted_at))}</span>
      </div>
    </Sheet>
  );
};

const PlanPage = ({ data, m }) => {
  const p = `m${m.month}_`;
  return (
    <Sheet testId={`worksheet-plan-month-${m.month}`}>
      <SheetHeader title={`Growth Plan Worksheet · Month ${m.month}`} sub="Workbook · monthly plan" data={data} />
      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4">
        <Blank label="Start date" value={fmtLong(m.from)} />
        <Blank label="End date" value={fmtLong(m.to)} />
      </div>
      {m.month > 1 && <div className="mt-3"><WbCheck k={`${p}same`} label="Same goal as last month" /></div>}
      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4">
        <Blank label="Topic of focus" k={`${p}topic`} />
        <Blank label="Area" k={`${p}area`} />
        <Blank label="My current rating" k={`${p}current`} />
        <Blank label="My next-level rating" k={`${p}next`} />
      </div>
      <div className="mt-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500">My plan to grow in this topic (one paragraph)</div>
      <WbText k={`${p}plan`} n={9} />
      <div className="mt-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500">How I will know I am growing</div>
      <WbText k={`${p}growing`} n={3} />
      <div className="mt-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Rating scale</div>
      <div className="mt-1.5"><ScaleKey compact /></div>
    </Sheet>
  );
};

const GRID = "grid grid-cols-[4.2rem_6.2rem_7.5rem_1fr] gap-3";

const DayRow = ({ d, showWeek }) => (
  <div className={`avoid-break ${GRID} items-end border-b border-slate-200 py-1.5 h-[0.5in]`} data-testid={`day-row-${d.dayIndex}`}>
    <span className="text-[11px] font-bold text-[#1F4266]">{showWeek ? `Week ${d.week}` : ""}</span>
    <span className="text-sm font-semibold text-slate-800">{fmtDay(d.date)}</span>
    <WbRating k={`d${d.dayIndex}_rating`} />
    <span className="flex"><WbLine k={`d${d.dayIndex}_step`} className="border-dotted" /></span>
  </div>
);

const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));

const TrackerPages = ({ data, m }) =>
  chunk(m.days, 16).map((days, p, all) => (
    <Sheet key={p} testId={`worksheet-tracker-month-${m.month}-page-${p + 1}`}>
      <SheetHeader title={`Daily Tracker Worksheet · Month ${m.month}`} sub={`Workbook · page ${p + 1} of ${all.length} · ${fmtLong(m.from)} – ${fmtLong(m.to)}`} data={data} />
      <div className={`mt-3 ${GRID} border-b-2 border-slate-300 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500`}>
        <span>Week</span><span>Date</span><span>How it went</span><span>What step did I take today to achieve my goal this month?</span>
      </div>
      {days.map((d, i) => <DayRow key={d.dayIndex} d={d} showWeek={i === 0 || (d.dayIndex % 7 === 0 && d.dayIndex < 91)} />)}
    </Sheet>
  ));

const SaveBanner = () => {
  const { editable, status } = useWorkbook();
  const msg = { saving: "Saving…", saved: "All changes saved to your report link.", error: "Couldn't save. Check your connection and try again." }[status];
  return (
    <div className="no-print rounded-2xl border border-dashed border-[#1F4266]/30 bg-[#1F4266]/5 p-5 text-sm text-slate-700" data-testid="workbook-banner">
      <span className="font-display font-semibold text-[#1F4266]">Growth workbook.</span>{" "}
      {editable
        ? "Type straight into these pages. Your answers save automatically, so you can come back to this link any time and print a filled-in copy."
        : "Each page prints on its own sheet. Answers the participant typed on their report link appear here."}
      {editable && msg && <span className="ml-2 font-semibold" data-testid="workbook-save-status">{msg}</span>}
    </div>
  );
};

export const TrackingWorksheet = ({ data, topics }) => {
  const months = monthPlan(data.submitted_at);
  return (
    <div className="worksheet-pages space-y-8" data-testid="tracking-worksheet">
      <SaveBanner />
      <KeyPage data={data} topics={topics || []} />
      {months.map((m) => (
        <div key={m.month} className="space-y-8" data-testid={`worksheet-month-${m.month}`}>
          <PlanPage data={data} m={m} />
          <TrackerPages data={data} m={m} />
        </div>
      ))}
      <NextStepPage data={data} />
    </div>
  );
};
